/**
 * Structured Logger for PinPad Operations
 * 
 * Provides structured logging with:
 * - Log levels (DEBUG, INFO, WARN, ERROR, CRITICAL)
 * - Sentry integration for error tracking
 * - Context enrichment (operation, user, session)
 * - Performance metrics
 * - Sanitization of sensitive data
 * 
 * @module utilities/pinpad-v2/logger
 */

import * as Sentry from '@sentry/react';
import { sanitizeResponse } from './validator';

/**
 * Log levels
 */
export const LogLevel = {
  DEBUG: 'DEBUG',
  INFO: 'INFO',
  WARN: 'WARN',
  ERROR: 'ERROR',
  CRITICAL: 'CRITICAL'
};

/**
 * Log level priorities (for filtering)
 */
const LOG_LEVEL_PRIORITY = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  CRITICAL: 4
};

/**
 * Current minimum log level (configurable)
 */
let currentLogLevel = LogLevel.INFO;

/**
 * Set minimum log level
 * @param {string} level - Log level
 */
export function setLogLevel(level) {
  if (LOG_LEVEL_PRIORITY[level] !== undefined) {
    currentLogLevel = level;
    console.info(`[Logger] Log level set to: ${level}`);
  }
}

/**
 * Check if should log based on level
 * @param {string} level - Log level to check
 * @returns {boolean} True if should log
 */
function shouldLog(level) {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[currentLogLevel];
}

/**
 * Enrich log context with standard fields
 * @param {Object} context - Context object
 * @returns {Object} Enriched context
 */
function enrichContext(context = {}) {
  return {
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV || 'development',
    version: process.env.REACT_APP_VERSION || 'unknown',
    ...context
  };
}

/**
 * Format log message
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Context object
 * @returns {Object} Formatted log entry
 */
function formatLog(level, message, context) {
  return {
    level,
    message,
    context: enrichContext(context)
  };
}

/**
 * Send log to console
 * @param {string} level - Log level
 * @param {Object} logEntry - Formatted log entry
 */
function logToConsole(level, logEntry) {
  const prefix = `[PinPad V2][${level}]`;
  const { message, context } = logEntry;

  switch (level) {
    case LogLevel.DEBUG:
      console.debug(prefix, message, context);
      break;
    case LogLevel.INFO:
      console.info(prefix, message, context);
      break;
    case LogLevel.WARN:
      console.warn(prefix, message, context);
      break;
    case LogLevel.ERROR:
    case LogLevel.CRITICAL:
      console.error(prefix, message, context);
      break;
    default:
      console.log(prefix, message, context);
  }
}

/**
 * Send log to Sentry
 * @param {string} level - Log level
 * @param {Object} logEntry - Formatted log entry
 */
function logToSentry(level, logEntry) {
  const { message, context } = logEntry;

  // Only send WARN, ERROR, and CRITICAL to Sentry
  if (LOG_LEVEL_PRIORITY[level] < LOG_LEVEL_PRIORITY[LogLevel.WARN]) {
    return;
  }

  // Map log level to Sentry severity
  const severityMap = {
    [LogLevel.WARN]: 'warning',
    [LogLevel.ERROR]: 'error',
    [LogLevel.CRITICAL]: 'fatal'
  };

  try {
    Sentry.captureMessage(message, {
      level: severityMap[level] || 'error',
      tags: {
        component: 'PinPad',
        version: 'v2',
        operation: context.operation || 'unknown'
      },
      contexts: {
        pinpad: sanitizeResponse(context)
      }
    });
  } catch (error) {
    console.error('[Logger] Failed to send to Sentry:', error);
  }
}

/**
 * Base logging function
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
function log(level, message, context = {}) {
  if (!shouldLog(level)) {
    return;
  }

  const logEntry = formatLog(level, message, context);

  // Log to console
  logToConsole(level, logEntry);

  // Log to Sentry for warnings and errors
  logToSentry(level, logEntry);
}

/**
 * Log debug message
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
export function logDebug(message, context = {}) {
  log(LogLevel.DEBUG, message, context);
}

/**
 * Log info message
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
export function logInfo(message, context = {}) {
  log(LogLevel.INFO, message, context);
}

/**
 * Log warning message
 * @param {string} message - Log message
 * @param {Object} context - Additional context
 */
export function logWarn(message, context = {}) {
  log(LogLevel.WARN, message, context);
}

/**
 * Log error message
 * @param {string} message - Log message
 * @param {Error|Object} error - Error object or context
 * @param {Object} additionalContext - Additional context
 */
export function logError(message, error = {}, additionalContext = {}) {
  const context = {
    ...additionalContext,
    error: error instanceof Error ? {
      message: error.message,
      stack: error.stack,
      code: error.code,
      name: error.name
    } : error
  };

  log(LogLevel.ERROR, message, context);

  // Send error to Sentry
  if (error instanceof Error) {
    try {
      Sentry.captureException(error, {
        tags: {
          component: 'PinPad',
          version: 'v2',
          operation: additionalContext.operation || 'unknown'
        },
        contexts: {
          pinpad: sanitizeResponse(additionalContext)
        }
      });
    } catch (sentryError) {
      console.error('[Logger] Failed to send error to Sentry:', sentryError);
    }
  }
}

/**
 * Log critical error message
 * @param {string} message - Log message
 * @param {Error|Object} error - Error object or context
 * @param {Object} additionalContext - Additional context
 */
export function logCritical(message, error = {}, additionalContext = {}) {
  const context = {
    ...additionalContext,
    error: error instanceof Error ? {
      message: error.message,
      stack: error.stack,
      code: error.code,
      name: error.name
    } : error
  };

  log(LogLevel.CRITICAL, message, context);

  // Send critical error to Sentry with high priority
  if (error instanceof Error) {
    try {
      Sentry.captureException(error, {
        level: 'fatal',
        tags: {
          component: 'PinPad',
          version: 'v2',
          operation: additionalContext.operation || 'unknown',
          critical: true
        },
        contexts: {
          pinpad: sanitizeResponse(additionalContext)
        }
      });
    } catch (sentryError) {
      console.error('[Logger] Failed to send critical error to Sentry:', sentryError);
    }
  }
}

/**
 * Log operation start
 * @param {string} operation - Operation name
 * @param {Object} context - Additional context
 * @returns {Object} Timer object with end() method
 */
export function logOperationStart(operation, context = {}) {
  const startTime = Date.now();

  logInfo(`Operation ${operation} started`, {
    operation,
    ...context
  });

  return {
    startTime,
    operation,
    context,
    end: (success = true, additionalContext = {}) => {
      const duration = Date.now() - startTime;
      const fullContext = {
        operation,
        duration,
        success,
        ...context,
        ...additionalContext
      };

      if (success) {
        logInfo(`Operation ${operation} completed successfully`, fullContext);
      } else {
        logError(`Operation ${operation} failed`, {}, fullContext);
      }

      return duration;
    }
  };
}

/**
 * Log performance metric
 * @param {string} metric - Metric name
 * @param {number} value - Metric value
 * @param {Object} context - Additional context
 */
export function logMetric(metric, value, context = {}) {
  logDebug(`Metric: ${metric}`, {
    metric,
    value,
    ...context
  });

  // Send custom metric to Sentry
  try {
    Sentry.addBreadcrumb({
      category: 'metric',
      message: `${metric}: ${value}`,
      level: 'info',
      data: {
        metric,
        value,
        ...sanitizeResponse(context)
      }
    });
  } catch (error) {
    console.error('[Logger] Failed to send metric to Sentry:', error);
  }
}

/**
 * Log user action
 * @param {string} action - Action name
 * @param {Object} context - Additional context
 */
export function logUserAction(action, context = {}) {
  logInfo(`User action: ${action}`, {
    action,
    type: 'user_action',
    ...context
  });

  // Add breadcrumb to Sentry
  try {
    Sentry.addBreadcrumb({
      category: 'user',
      message: action,
      level: 'info',
      data: sanitizeResponse(context)
    });
  } catch (error) {
    console.error('[Logger] Failed to add breadcrumb to Sentry:', error);
  }
}

/**
 * Set user context for logging
 * @param {Object} user - User information
 */
export function setUserContext(user) {
  try {
    Sentry.setUser({
      id: user.id,
      username: user.username || user.name,
      email: user.email
    });
    logDebug('User context set', { userId: user.id });
  } catch (error) {
    console.error('[Logger] Failed to set user context:', error);
  }
}

/**
 * Clear user context
 */
export function clearUserContext() {
  try {
    Sentry.setUser(null);
    logDebug('User context cleared');
  } catch (error) {
    console.error('[Logger] Failed to clear user context:', error);
  }
}

/**
 * Add custom context
 * @param {string} key - Context key
 * @param {Object} data - Context data
 */
export function addContext(key, data) {
  try {
    Sentry.setContext(key, sanitizeResponse(data));
    logDebug(`Context added: ${key}`, data);
  } catch (error) {
    console.error('[Logger] Failed to add context:', error);
  }
}

/**
 * Create a child logger with preset context
 * @param {Object} presetContext - Context to include in all logs
 * @returns {Object} Child logger instance
 */
export function createLogger(presetContext = {}) {
  return {
    debug: (message, context = {}) => logDebug(message, { ...presetContext, ...context }),
    info: (message, context = {}) => logInfo(message, { ...presetContext, ...context }),
    warn: (message, context = {}) => logWarn(message, { ...presetContext, ...context }),
    error: (message, error, context = {}) => logError(message, error, { ...presetContext, ...context }),
    critical: (message, error, context = {}) => logCritical(message, error, { ...presetContext, ...context }),
    operationStart: (operation, context = {}) => logOperationStart(operation, { ...presetContext, ...context }),
    metric: (metric, value, context = {}) => logMetric(metric, value, { ...presetContext, ...context }),
    userAction: (action, context = {}) => logUserAction(action, { ...presetContext, ...context })
  };
}

export default {
  setLogLevel,
  logDebug,
  logInfo,
  logWarn,
  logError,
  logCritical,
  logOperationStart,
  logMetric,
  logUserAction,
  setUserContext,
  clearUserContext,
  addContext,
  createLogger,
  LogLevel
};
